import React, { useEffect, useMemo } from 'react';
import './soaTableRenderer.css';
import { resolveSoaTableForRender } from '../utils/resolveSoaTableColumns';

/** Same semantics as backend `_column_key`: join non-empty l1–l4 with `|` (omit null/empty). Values may be numbers from JSON — coerce to string. */
function buildColumnKey(col) {
  const parts = [col?.l1, col?.l2, col?.l3, col?.l4]
    .map((p) => {
      if (p == null || p === "") return "";
      const s = String(p).trim();
      return s;
    })
    .filter((p) => p.length > 0);
  return parts.join("|");
}

function normalizeKey(key) {
  return String(key || "").trim();
}

/** Optional display tiers — independent of l1–l4 used for columnKey / grid marks. */
function getColumnDisplayTiers(col) {
  if (Array.isArray(col?.headerLayers) && col.headerLayers.length >= 3) {
    return {
      t1: String(col.headerLayers[0] ?? "").trim(),
      t2: String(col.headerLayers[1] ?? "").trim(),
      t3: String(col.headerLayers[2] ?? col?.window ?? "").trim() || "—",
    };
  }
  if (
    col?.headerTier1 !== undefined ||
    col?.headerTier2 !== undefined ||
    col?.headerTier3 !== undefined
  ) {
    const ht3 = col.headerTier3;
    const hasHt3 = ht3 != null && String(ht3).trim() !== "";
    const t3Resolved = hasHt3
      ? String(ht3).trim()
      : String(col?.window ?? col?.l3 ?? "")
          .trim() || "—";
    return {
      t1: String(col.headerTier1 ?? "").trim(),
      t2: String(col.headerTier2 ?? "").trim(),
      t3: t3Resolved,
    };
  }
  return null;
}

/** Default: layered schedule headers whenever there are columns (opt out with layeredColumnHeaders: false). */
function shouldUseLayeredHeaders(table, columns) {
  if (table?.layeredColumnHeaders === false) return false;
  return columns.length > 0;
}

/**
 * Display tiers for thead — never shows raw `l1|l2|l3` pipe keys in the UI.
 * - Explicit headerTier / headerLayers win when set.
 * - If the model repeats `headerTier1` on every column but only fills `headerTier2` on the first
 *   column, empty tier2 must fall back to **`l2`** (stable column identity) so "Cycle 2 / subsequent"
 *   rows still render under Treatment Cycles.
 * - If `l1` contains "|" (model stuffed hierarchy in one field), split into tiers.
 * - Else use l1, l2, window/l3 as three rows.
 */
function deriveDisplayTiers(col) {
  const l1 = String(col?.l1 ?? "").trim();
  const l2 = String(col?.l2 ?? "").trim();
  const win = String(col?.window ?? "").trim();
  const l3 = String(col?.l3 ?? "").trim();
  const bottom = win || l3 || "—";

  const explicit = getColumnDisplayTiers(col);
  if (explicit) {
    const t1 = explicit.t1 || l1 || "—";
    const t2 = String(explicit.t2 ?? "").trim() || l2;
    let t3 = String(explicit.t3 ?? "").trim();
    if (!t3 || t3 === "—") t3 = bottom || "—";
    return { t1, t2, t3 };
  }

  if (l1.includes("|")) {
    const segs = l1
      .split("|")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (segs.length >= 3) {
      return { t1: segs[0], t2: segs[1], t3: segs[2] || bottom };
    }
    if (segs.length === 2) {
      return { t1: segs[0], t2: segs[1], t3: bottom };
    }
    if (segs.length === 1) {
      return { t1: segs[0], t2: l2, t3: bottom };
    }
  }

  return {
    t1: l1 || "—",
    t2: l2,
    t3: bottom,
  };
}

function buildTierRows(columns, layered) {
  if (!layered) return columns.map(() => null);
  return columns.map((col) => deriveDisplayTiers(col));
}

/** Consecutive columns with same non-empty tier1 → colspan (e.g. Treatment Cycles over 3 cycles). Empty tier1 does not merge across columns. */
function buildMergedTier1Cells(tiers) {
  const cells = [];
  let i = 0;
  const n = tiers.length;
  while (i < n) {
    const raw = String(tiers[i].t1 ?? "").trim();
    const label = raw || "—";
    let j = i + 1;
    if (raw) {
      while (j < n && String(tiers[j].t1 ?? "").trim() === raw) j++;
    } else {
      j = i + 1;
    }
    cells.push({ start: i, colspan: j - i, label });
    i = j;
  }
  return cells;
}

/** Normalize for comparing tier labels (collapse whitespace). */
function normalizeTier2Label(s) {
  return String(s ?? "")
    .trim()
    .replace(/\s+/g, " ");
}

/**
 * Consecutive columns in `columnIndices` with identical tier2 text merge on the tier2 header row
 * (e.g. four intra-cycle visits under one "Cycle 1 (42 days)" while tier3 stays per-column).
 */
function buildMergedTier2Cells(columnIndices, tierRows) {
  const segments = [];
  const arr = columnIndices;
  let k = 0;
  while (k < arr.length) {
    const i0 = arr[k];
    const raw = normalizeTier2Label(tierRows[i0]?.t2 ?? "");
    let kk = k + 1;
    if (raw) {
      while (
        kk < arr.length &&
        normalizeTier2Label(tierRows[arr[kk]]?.t2 ?? "") === raw
      ) {
        kk++;
      }
    }
    const colspan = kk - k;
    segments.push({
      startIdx: i0,
      colspan,
      label: raw || "—",
    });
    k = kk;
  }
  return segments;
}

function tier2Empty(t) {
  return !String(t?.t2 ?? "").trim();
}

/** Strip any leading letter/number label or trailing power-style marker — plain prose only. */
function stripFootnoteLabel(raw) {
  return String(raw || "")
    .replace(/^\s*[\[\(]?\d+[\]\)]?[\.\):]\s*/, "")
    .replace(/^\s*[\[\(]?[a-zA-Z]{1,3}[\]\)]?[\.\):]\s*/, "")
    .replace(/\s*[\^⁺]?[¹²³⁴⁵⁶⁷⁸⁹⁰]+\s*$/u, "")
    .replace(/\s*\^\d+\s*$/g, "")
    .trim();
}

/**
 * Three-layer thead + Banovo-style merges (general, data-driven):
 * - Always three logical tiers (tier1 / tier2 / tier3).
 * - Same tier1 across consecutive cols → colspan on tier1 row (e.g. Treatment Cycles).
 * - Same tier2 across consecutive cols (within the open column strip) → colspan on tier2 row (e.g. one "Cycle 1 (42 days)" over Day 1/15/22/29).
 * - Single column + tier2 empty → one cell rowSpan=3 with tier1 + tier3 stacked (Screening / follow-up).
 * - Single column + tier2 set → three cells, one per tier row.
 */
function buildTripleMergedPlan(tierRows, mergedTier1) {
  const n = tierRows.length;
  const rowspan3 = Array(n).fill(false);

  const row1Segments = mergedTier1.map((cell) => {
    const { start, colspan, label } = cell;
    const t0 = tierRows[start];
    if (colspan === 1 && tier2Empty(t0)) {
      rowspan3[start] = true;
      return { kind: "rowspan3", start, colspan: 1, label };
    }
    if (colspan > 1) {
      return { kind: "groupTop", start, colspan, label };
    }
    return { kind: "tier1Only", start, colspan: 1, label };
  });

  const openColumnIndices = [];
  for (let j = 0; j < n; j++) {
    if (!rowspan3[j]) openColumnIndices.push(j);
  }

  /** All visit cols are full-height merged — one thead row only (tiers shown stacked inside each cell). */
  const singleHeaderRow =
    openColumnIndices.length === 0 &&
    row1Segments.length > 0 &&
    row1Segments.every((s) => s.kind === "rowspan3");

  return { rowspan3, row1Segments, openColumnIndices, singleHeaderRow };
}

/** Plain 3×N grid: every column gets three separate cells (no colspan/rowspan merges). */
function useSimpleTripleHeaderRows(table) {
  const v = table?.layeredColumnHeaders;
  return v === "triple" || v === "simpleTriple";
}

function Badge({ label, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    green: "bg-emerald-50 text-emerald-800 border-emerald-200",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${tones[tone] || tones.slate}`}>
      {label}
    </span>
  );
}

function Mark({ value }) {
  const v = String(value || "").trim();
  const display = v || "—";
  return <span className="font-semibold text-slate-800">{display}</span>;
}

/** Full grid: vertical + horizontal lines between all cells (SOA schedule style). */
const B = {
  row: "border-b border-slate-300",
  vline: "border-r border-slate-300",
  vlineStrong: "border-r-2 border-slate-400",
  hLineThick: "border-b-2 border-slate-300",
};

export default function SoaTableRenderer({ table, allTables, variant = "default", hideTitle = false }) {
  const isProtocol = variant === "protocol";
  const titleClass = "text-sm font-semibold text-slate-900";
  const scopeClass = isProtocol
    ? "mt-0.5 text-[11px] leading-relaxed text-slate-600"
    : "mt-0.5 text-xs text-slate-600";
  const shellClass = isProtocol
    ? "min-h-0 min-w-0"
    : "min-h-0 min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm";
  const siblingTables = useMemo(() => {
    if (Array.isArray(allTables) && allTables.length) return allTables;
    return table ? [table] : [];
  }, [allTables, table]);
  const displayTable = useMemo(
    () => resolveSoaTableForRender(table, siblingTables),
    [table, siblingTables],
  );
  const columns = Array.isArray(displayTable?.columns) ? displayTable.columns : [];
  const rowGroups = Array.isArray(displayTable?.rowGroups) ? displayTable.rowGroups : [];
  const grid = Array.isArray(displayTable?.grid) ? displayTable.grid : [];
  const footnotes = Array.isArray(displayTable?.footnotes) ? displayTable.footnotes : [];

  const layered = shouldUseLayeredHeaders(table, columns);
  const tierRows = useMemo(() => buildTierRows(columns, layered), [columns, layered]);

  const columnKeys = useMemo(() => columns.map((c) => buildColumnKey(c)), [columns]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && columns.length) {
      const payload = columns.map((col, idx) => ({
        index: idx,
        l1: col?.l1 ?? null,
        l2: col?.l2 ?? null,
        l3: col?.l3 ?? null,
        l4: col?.l4 ?? null,
        columnKey: buildColumnKey(col),
      }));
      console.log(`[SOA ${table?.tableId || table?.label || 'table'}] columns l1–l4 → columnKey`, payload);
    }
  }, [columns, table?.tableId, table?.label]);

  const mergedTier1 = useMemo(() => (layered && tierRows.length ? buildMergedTier1Cells(tierRows) : []), [layered, tierRows]);
  const simpleTriple = useSimpleTripleHeaderRows(table);
  const tripleMergedPlan = useMemo(() => {
    if (!layered || tierRows.length !== columns.length || columns.length === 0 || simpleTriple) return null;
    return buildTripleMergedPlan(tierRows, mergedTier1);
  }, [layered, tierRows, columns.length, mergedTier1, simpleTriple]);

  /** Tier2 row: merge consecutive cols with same label (e.g. one "Cycle 1 (42 days)" over Day 1 / 15 / 22 / 29). */
  const mergedTier2FullRow = useMemo(() => {
    if (!tierRows.length) return [];
    return buildMergedTier2Cells(
      tierRows.map((_, i) => i),
      tierRows,
    );
  }, [tierRows]);

  const mergedTier2OpenRow = useMemo(() => {
    if (!tripleMergedPlan?.openColumnIndices?.length) return [];
    return buildMergedTier2Cells(tripleMergedPlan.openColumnIndices, tierRows);
  }, [tripleMergedPlan, tierRows]);

  const markLookup = useMemo(() => {
    const byAssessment = new Map();
    grid.forEach((row) => {
      const assessment = normalizeKey(row?.assessment);
      if (!assessment) return;
      const marks = Array.isArray(row?.marks) ? row.marks : [];
      const byCol = new Map();
      marks.forEach((m) => {
        const colKey = normalizeKey(m?.columnKey);
        if (!colKey) return;
        byCol.set(colKey, { value: m?.value ?? "", noteRef: m?.noteRef ?? null });
      });
      byAssessment.set(assessment, byCol);
    });
    return byAssessment;
  }, [grid]);

  /** id → plain footnote text (label/superscript stripped) for lookup by mark.noteRef. */
  const footnoteById = useMemo(() => {
    const map = new Map();
    footnotes.forEach((fn, idx) => {
      const id = normalizeKey(fn?.id ?? idx + 1);
      const text = stripFootnoteLabel(fn?.text ?? fn ?? "");
      if (id && text) map.set(id, text);
    });
    return map;
  }, [footnotes]);

  /**
   * Footnote ids actually linked to a row — these render under their activity,
   * not in the table-level block. A footnote can be bound two ways: per-cell
   * (`mark.noteRef`, the S9 chat editor's own authoring shape) or per-row
   * (`assessment.footnoteRefs`/`footnoteRef`, the shape the USDM-catalogue
   * import path produces — see s9SoaAdapter.service.js's s9TableToLegacy,
   * which sets `footnoteRefs` on the assessment, not on individual marks).
   * Missing the row-level form here meant every USDM-catalogue-sourced
   * footnote fell through to the generic table-level block regardless of how
   * clearly it was actually bound to a row in the source data.
   */
  const referencedFootnoteIds = useMemo(() => {
    const set = new Set();
    grid.forEach((row) => {
      const marks = Array.isArray(row?.marks) ? row.marks : [];
      marks.forEach((m) => {
        if (m?.noteRef != null && String(m.noteRef).trim()) {
          set.add(normalizeKey(m.noteRef));
        }
      });
    });
    rowGroups.forEach((group) => {
      (group?.assessments || []).forEach((a) => {
        const refs = Array.isArray(a?.footnoteRefs)
          ? a.footnoteRefs
          : a?.footnoteRef
            ? [a.footnoteRef]
            : [];
        refs.forEach((ref) => {
          if (ref != null && String(ref).trim()) set.add(normalizeKey(ref));
        });
      });
    });
    return set;
  }, [grid, rowGroups]);

  /** Only unlinked (general) footnotes remain in the table-level block — linked ones show per activity. */
  const generalFootnotes = useMemo(() => {
    return footnotes.filter((fn, idx) => {
      const id = normalizeKey(fn?.id ?? idx + 1);
      return !referencedFootnoteIds.has(id);
    });
  }, [footnotes, referencedFootnoteIds]);

  /** Fallback when layered is off — still avoid showing pipe-joined keys in one blob. */
  const theadFlat = (
    <tr>
      <th
        className={`min-w-[220px] ${B.row} ${B.vlineStrong} bg-slate-50 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700`}
      >
        Assessment
      </th>
      {columns.map((col, idx) => {
        const key = columnKeys[idx] || `col-${idx}`;
        const d = deriveDisplayTiers(col);
        return (
          <th
            key={key}
            className={`min-w-[100px] ${B.row} ${B.vline} bg-slate-50 px-2 py-2 align-bottom`}
          >
            <div className="flex flex-col items-center gap-0.5 text-center">
              <span className="text-[11px] font-semibold leading-tight text-slate-800">{d.t1}</span>
              {d.t2 ? <span className="text-[10px] font-medium leading-snug text-slate-600">{d.t2}</span> : null}
              <span className="text-[10px] leading-snug text-slate-500">{d.t3}</span>
            </div>
          </th>
        );
      })}
    </tr>
  );

  const showLayered = Boolean(layered && tierRows.length === columns.length && columns.length > 0);
  const showSimpleTripleThead = Boolean(showLayered && simpleTriple);
  const showTripleMergedThead = Boolean(showLayered && tripleMergedPlan);

  /** Plain 3-row thead — opt in with `layeredColumnHeaders: "triple"` or `"simpleTriple"`. */
  const theadSimpleTriple = showSimpleTripleThead ? (
    <>
      <tr>
        <th
          rowSpan={3}
          className={`min-w-[220px] ${B.vlineStrong} ${B.hLineThick} bg-slate-50 px-3 py-2 align-middle text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700`}
        >
          Assessment
        </th>
        {mergedTier1.map((cell) => (
          <th
            key={cell.start}
            colSpan={cell.colspan}
            className={`${B.row} ${B.vline} bg-slate-100 px-2 py-2 text-center text-[11px] font-semibold leading-tight text-slate-900`}
          >
            {cell.label}
          </th>
        ))}
      </tr>
      <tr>
        {mergedTier2FullRow.map((seg) => (
          <th
            key={`t2-${columnKeys[seg.startIdx] ?? seg.startIdx}`}
            colSpan={seg.colspan}
            className={`${B.row} ${B.vline} bg-slate-50 px-2 py-1.5 text-center text-[10px] font-semibold leading-snug text-slate-800`}
          >
            {seg.label !== "—" ? <span className="text-slate-800">{seg.label}</span> : <span className="text-slate-300"> </span>}
          </th>
        ))}
      </tr>
      <tr>
        {tierRows.map((t, idx) => (
          <th
            key={`t3-${idx}`}
            className={`${B.hLineThick} ${B.vline} bg-white px-2 py-1.5 text-center text-[10px] font-medium leading-snug text-slate-600`}
          >
            {t.t3 || "—"}
          </th>
        ))}
      </tr>
    </>
  ) : null;

  /** Three thead rows (tier1 / tier2 / tier3) + colspan + rowspan merges — default when layered. */
  const theadTripleMerged =
    showTripleMergedThead && tripleMergedPlan ? (
      tripleMergedPlan.singleHeaderRow ? (
        <tr>
          <th
            className={`min-w-[220px] ${B.row} ${B.vlineStrong} ${B.hLineThick} bg-slate-50 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700`}
          >
            Assessment
          </th>
          {tripleMergedPlan.row1Segments.map((seg) => {
            const t = tierRows[seg.start];
            const ck = columnKeys[seg.start] || `col-${seg.start}`;
            return (
              <th key={ck} className={`min-w-[100px] ${B.row} ${B.vline} bg-slate-50 px-2 py-2 align-middle`}>
                <div className="flex flex-col items-center justify-between gap-1 py-0.5 text-center">
                  <span className="text-[11px] font-semibold leading-tight text-slate-900">{t.t1 || seg.label}</span>
                  <span className="text-[10px] text-slate-400"> </span>
                  <span className="text-[10px] font-medium leading-snug text-slate-600">{t.t3 || "—"}</span>
                </div>
              </th>
            );
          })}
        </tr>
      ) : (
        <>
          <tr>
            <th
              rowSpan={3}
              className={`min-w-[220px] ${B.vlineStrong} ${B.hLineThick} bg-slate-50 px-3 py-2 align-middle text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700`}
            >
              Assessment
            </th>
            {tripleMergedPlan.row1Segments.map((seg) => {
              const ck = columnKeys[seg.start] || `seg-${seg.start}`;
              if (seg.kind === "rowspan3") {
                const t = tierRows[seg.start];
                return (
                  <th
                    key={ck}
                    rowSpan={3}
                    className={`min-w-[100px] ${B.vline} bg-slate-50 px-2 py-2 align-middle ${B.hLineThick}`}
                  >
                    <div className="flex h-full min-h-[4.5rem] flex-col items-center justify-between gap-1 text-center">
                      <span className="text-[11px] font-semibold leading-tight text-slate-900">{t.t1 || seg.label}</span>
                      <span className="text-[10px] font-medium leading-snug text-slate-500"> </span>
                      <span className="text-[10px] font-medium leading-snug text-slate-600">{t.t3 || "—"}</span>
                    </div>
                  </th>
                );
              }
              if (seg.kind === "groupTop") {
                return (
                  <th
                    key={ck}
                    colSpan={seg.colspan}
                    className={`${B.row} ${B.vline} bg-slate-100 px-2 py-2 text-center text-[11px] font-semibold leading-tight text-slate-900`}
                  >
                    {seg.label}
                  </th>
                );
              }
              const t = tierRows[seg.start];
              return (
                <th key={ck} className={`${B.row} ${B.vline} bg-slate-50 px-2 py-2 text-center align-bottom`}>
                  <span className="text-[11px] font-semibold leading-tight text-slate-900">{seg.label || t.t1}</span>
                </th>
              );
            })}
          </tr>
          <tr>
            {mergedTier2OpenRow.map((seg) => (
              <th
                key={`t2-${columnKeys[seg.startIdx] ?? seg.startIdx}`}
                colSpan={seg.colspan}
                className={`${B.row} ${B.vline} bg-slate-50 px-2 py-1.5 text-center text-[10px] font-semibold leading-snug text-slate-800`}
              >
                {seg.label !== "—" ? <span>{seg.label}</span> : <span className="text-slate-300"> </span>}
              </th>
            ))}
          </tr>
          <tr>
            {tripleMergedPlan.openColumnIndices.map((colIdx) => {
              const t = tierRows[colIdx];
              const ck = columnKeys[colIdx] || `t3-${colIdx}`;
              return (
                <th key={ck} className={`${B.hLineThick} ${B.vline} bg-white px-2 py-1.5 text-center text-[10px] font-medium leading-snug text-slate-600`}>
                  {t.t3 || "—"}
                </th>
              );
            })}
          </tr>
        </>
      )
    ) : null;

  return (
    <div className={`s9-soa-root ${shellClass}`}>
      {!hideTitle ? (
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className={titleClass}>1.3 Schedule of Activities</div>
            {(displayTable?.scope || table?.scope) ? <div className={scopeClass}>{displayTable?.scope || table?.scope}</div> : null}
          </div>
          {!isProtocol ? (
            <div className="flex items-center gap-1">
              <Badge label={displayTable?.tableId || table?.tableId || "T?"} />
              {(displayTable?.inheritsFromTableId || table?.inheritsFromTableId) ? (
                <Badge label={`inherits ${displayTable?.inheritsFromTableId || table?.inheritsFromTableId}`} tone="amber" />
              ) : (
                <Badge label="primary" tone="green" />
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={`${hideTitle ? "" : isProtocol ? "" : "mt-3"} overflow-auto rounded-lg border border-slate-200`}>
        <table className={`min-w-full border-collapse text-left ${isProtocol ? "text-[11px] leading-snug text-slate-800" : "text-xs"}`}>
          <thead className="sticky top-0 z-10">
            {showSimpleTripleThead ? theadSimpleTriple : showTripleMergedThead ? theadTripleMerged : theadFlat}
          </thead>
          <tbody>
            {rowGroups.length ? (
              rowGroups.map((group) => {
                const assessments = Array.isArray(group?.assessments) ? group.assessments : [];
                return (
                  <React.Fragment key={group?.group || Math.random().toString(36)}>
                    <tr>
                      <td colSpan={1 + columns.length} className="border-b border-slate-200 bg-slate-50 px-4 py-2">
                        <div className="text-xs font-bold uppercase tracking-wide text-slate-600">{group?.group || "Group"}</div>
                      </td>
                    </tr>
                    {assessments.map((a, aIdx) => {
                      const name = normalizeKey(a?.name);
                      const rowKey =
                        `${String(group?.group || "grp")}:${name || `unnamed-${aIdx}`}:${aIdx}`;
                      const status = String(a?.status || "").toLowerCase();
                      const tone = status === "confirmed" ? "green" : "amber";
                      const marksForAssessment = markLookup.get(name) || new Map();
                      const rowNoteTexts = [];
                      const seenNoteIds = new Set();
                      marksForAssessment.forEach((cell) => {
                        if (cell?.noteRef == null) return;
                        const noteId = normalizeKey(cell.noteRef);
                        if (!noteId || seenNoteIds.has(noteId)) return;
                        seenNoteIds.add(noteId);
                        const text = footnoteById.get(noteId);
                        if (text) rowNoteTexts.push(text);
                      });
                      // Row-level footnote bindings (assessment.footnoteRefs/footnoteRef) —
                      // see referencedFootnoteIds' comment above for why this is a
                      // second, separate binding mechanism from cell.noteRef.
                      const rowLevelFootnoteRefs = Array.isArray(a?.footnoteRefs)
                        ? a.footnoteRefs
                        : a?.footnoteRef
                          ? [a.footnoteRef]
                          : [];
                      rowLevelFootnoteRefs.forEach((ref) => {
                        const noteId = normalizeKey(ref);
                        if (!noteId || seenNoteIds.has(noteId)) return;
                        seenNoteIds.add(noteId);
                        const text = footnoteById.get(noteId);
                        if (text) rowNoteTexts.push(text);
                      });
                      return (
                        <React.Fragment key={rowKey}>
                          <tr className="hover:bg-blue-50/30">
                            <td className={`${rowNoteTexts.length ? "border-b-0" : B.row} ${B.vlineStrong} bg-white px-4 py-2.5 align-middle`}>
                              <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="text-xs font-medium text-slate-800">{name || "Unnamed assessment"}</div>
                                  {/* source/status are authoring-time provenance hints for the S9 chat
                                      editor (see soaProvenance extension attributes) — never shown in the
                                      published protocol document. */}
                                  {!isProtocol && a?.source ? <div className="mt-0.5 text-[10px] text-slate-400">Source: {a.source}</div> : null}
                                </div>
                                {!isProtocol ? <Badge label={status || "provisional"} tone={tone} /> : null}
                              </div>
                            </td>
                            {columnKeys.map((ck, idx) => {
                              const cell = marksForAssessment.get(ck) || null;
                              return (
                                <td key={`${name}-${idx}`} className={`${rowNoteTexts.length ? "border-b-0" : B.row} ${B.vline} bg-white px-3 py-2.5 text-center align-middle text-slate-800`}>
                                  {/* Plain X only — no footnote id / power superscript on marks */}
                                  <Mark value={cell?.value} />
                                </td>
                              );
                            })}
                          </tr>
                          {rowNoteTexts.length ? (
                            <tr className="hover:bg-blue-50/30">
                              <td colSpan={1 + columns.length} className={`${B.row} bg-white px-4 pb-2 pt-0 align-top`}>
                                <div className="flex flex-col gap-0.5 pl-0">
                                  {rowNoteTexts.map((text, noteIdx) => (
                                    <div key={noteIdx} className="text-[11px] italic leading-snug text-slate-500">
                                      {text}
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={1 + columns.length} className="px-3 py-6 text-center text-xs text-slate-600">
                  No row groups available yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {generalFootnotes.length ? (
        <div className="mt-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Key Notes</div>
          <div className="mt-2 grid gap-2">
            {generalFootnotes.map((fn, idx) => {
              const text = stripFootnoteLabel(fn?.text ?? fn ?? "");
              if (!text) return null;
              return (
                <div
                  key={fn?.id ?? `kn-${idx}`}
                  className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700"
                >
                  {text}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
